export interface FormDataBuilderOptions {
  IncludeNullValues?: boolean;

  MaximumFieldCount?: number;
}

interface FormDataBuilderContext {
  FormData: FormData;

  IncludeNullValues: boolean;

  MaximumFieldCount: number;

  FieldCount: number;

  Parents: WeakSet<object>;
}

/**
 * Converts an object into FormData.
 *
 * Supports:
 * - Strings
 * - Numbers
 * - Booleans
 * - BigInts
 * - Files
 * - Blobs
 * - Dates
 * - URLs
 * - Arrays
 * - Nested objects
 * - ArrayBuffer
 * - Typed arrays
 */
export function BuildFormData<T extends object>(
  source: T,
  options?: FormDataBuilderOptions,
): FormData {
  if (!source) {
    throw new Error("The object supplied to BuildFormData is required.");
  }

  const context: FormDataBuilderContext = {
    FormData: new FormData(),

    IncludeNullValues: options?.IncludeNullValues ?? false,

    MaximumFieldCount: options?.MaximumFieldCount ?? 1000,

    FieldCount: 0,

    Parents: new WeakSet<object>(),
  };

  Object.entries(source).forEach(([propertyName, value]) => {
    AppendValue(context, propertyName, value);
  });

  return context.FormData;
}

function AppendValue(
  context: FormDataBuilderContext,
  propertyPath: string,
  value: unknown,
): void {
  if (value === null || value === undefined) {
    if (context.IncludeNullValues) {
      AppendFormValue(context, propertyPath, "");
    }

    return;
  }

  /*
   * File and Blob must be handled before the
   * ordinary object check.
   */
  if (IsFile(value)) {
    AppendFormFile(context, propertyPath, value, value.name);

    return;
  }

  if (IsBlob(value)) {
    AppendFormFile(
      context,
      propertyPath,
      value,
      `${GetPropertyName(propertyPath)}.bin`,
    );

    return;
  }

  if (value instanceof Date) {
    AppendFormValue(context, propertyPath, value.toISOString());

    return;
  }

  if (value instanceof URL) {
    AppendFormValue(context, propertyPath, value.toString());

    return;
  }

  if (value instanceof ArrayBuffer) {
    AppendFormFile(
      context,
      propertyPath,
      new Blob([value]),
      `${GetPropertyName(propertyPath)}.bin`,
    );

    return;
  }

  /*
   * This covers Uint8Array, Int32Array,
   * DataView and other ArrayBuffer views.
   *
   * value.buffer may be an ArrayBuffer or
   * SharedArrayBuffer, so its bytes are copied
   * into a guaranteed ArrayBuffer before creating
   * the Blob.
   */
  if (ArrayBuffer.isView(value)) {
    const copiedBuffer = new ArrayBuffer(value.byteLength);

    const copiedBytes = new Uint8Array(copiedBuffer);

    const sourceBytes = new Uint8Array(
      value.buffer,
      value.byteOffset,
      value.byteLength,
    );

    copiedBytes.set(sourceBytes);

    AppendFormFile(
      context,
      propertyPath,
      new Blob([copiedBuffer]),
      `${GetPropertyName(propertyPath)}.bin`,
    );

    return;
  }

  if (Array.isArray(value)) {
    AppendArray(context, propertyPath, value);

    return;
  }

  /*
   * Only ordinary JavaScript objects are
   * recursively expanded.
   */
  if (IsPlainObject(value)) {
    AppendObject(context, propertyPath, value);

    return;
  }

  if (IsPrimitive(value)) {
    AppendFormValue(context, propertyPath, String(value));

    return;
  }

  throw new Error(
    `The property "${propertyPath}" cannot be converted to FormData. ` +
      `Unsupported type: ${GetValueType(value)}.`,
  );
}

function AppendArray(
  context: FormDataBuilderContext,
  propertyPath: string,
  values: unknown[],
): void {
  RegisterParent(context, values, propertyPath);

  try {
    values.forEach((value, index) => {
      AppendValue(context, `${propertyPath}[${index}]`, value);
    });
  } finally {
    context.Parents.delete(values);
  }
}

function AppendObject(
  context: FormDataBuilderContext,
  propertyPath: string,
  value: Record<string, unknown>,
): void {
  RegisterParent(context, value, propertyPath);

  try {
    Object.entries(value).forEach(([propertyName, propertyValue]) => {
      AppendValue(context, `${propertyPath}.${propertyName}`, propertyValue);
    });
  } finally {
    context.Parents.delete(value);
  }
}

function AppendFormValue(
  context: FormDataBuilderContext,
  propertyPath: string,
  value: string,
): void {
  IncreaseFieldCount(context, propertyPath);

  context.FormData.append(propertyPath, value);
}

function AppendFormFile(
  context: FormDataBuilderContext,
  propertyPath: string,
  file: Blob,
  fileName: string,
): void {
  IncreaseFieldCount(context, propertyPath);

  context.FormData.append(propertyPath, file, fileName);
}

function IncreaseFieldCount(
  context: FormDataBuilderContext,
  propertyPath: string,
): void {
  context.FieldCount += 1;

  if (context.FieldCount > context.MaximumFieldCount) {
    throw new Error(
      `FormData exceeded ${context.MaximumFieldCount} fields ` +
        `while processing "${propertyPath}". ` +
        "The submitted object probably contains an unintended property.",
    );
  }
}

function RegisterParent(
  context: FormDataBuilderContext,
  value: object,
  propertyPath: string,
): void {
  if (context.Parents.has(value)) {
    throw new Error(`Circular reference detected at "${propertyPath}".`);
  }

  context.Parents.add(value);
}

function IsFile(value: unknown): value is File {
  return typeof File !== "undefined" && value instanceof File;
}

function IsBlob(value: unknown): value is Blob {
  return typeof Blob !== "undefined" && value instanceof Blob;
}

function IsPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);

  return prototype === Object.prototype || prototype === null;
}

function IsPrimitive(
  value: unknown,
): value is string | number | boolean | bigint {
  return (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint"
  );
}

function GetPropertyName(propertyPath: string): string {
  const pathParts = propertyPath.split(".");

  return pathParts[pathParts.length - 1] ?? "file";
}

function GetValueType(value: unknown): string {
  if (typeof value === "object" && value !== null) {
    return value.constructor?.name ?? "Object";
  }

  return typeof value;
}

export async function hash(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value);

  const buffer = await window.crypto.subtle.digest("SHA-256", bytes);

  return Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}
/**
 * Development utility for inspecting generated FormData.
 */
export function LogFormData(formData: FormData): void {
  if (!import.meta.env.DEV) {
    return;
  }

  const entries = Array.from(formData.entries());

  console.log("FormData field count:", entries.length);

  console.table(
    entries.map(([key, value]) => ({
      Key: key,

      Type: value instanceof File ? "File" : typeof value,

      Value:
        value instanceof File
          ? `${value.name} (${value.size} bytes)`
          : String(value).slice(0, 200),
    })),
  );
}
